import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useThemeMediaQuery } from 'hooks/useThemeMediaQuery'
import { Button, Flex, Text, TextVariant } from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'

import { CopyToClipboard } from 'react-copy-to-clipboard'
import { Check, Copy } from 'react-feather'
import { useGetLicenseKeyQuery } from 'state/services/indexNode/indexNode'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { indexNodeStore } from 'mobxStores/IndexNodeStore'
import { observer } from 'mobx-react-lite'
import { Popover } from '@mprolab/uikit/src/components/Navbar/Popover'

const MainWrapper = styled(Flex)`
  width: 100%;
  margin-top: 24px;
  gap: 4px;
  padding: 8px 16px;
  border-radius: 8px;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.gray10};
  ${({ theme }) => theme.mediaQueries.mobile} {
    background: none;
    padding: 0;
  }
`

const IconWrapper = styled(Flex)`
  cursor: pointer;
`

interface SuccessApiKeyProps {
  name: string
  licenseId: string
}

const SuccessApiKey: React.FC<SuccessApiKeyProps> = observer(({ name, licenseId }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const isMobile = useThemeMediaQuery('mobile')
  const [isCopied, setIsCopied] = useState<boolean>(false)
  const { account } = useWeb3React()
  const { accessToken } = indexNodeStore
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const { data, isFetching, isError } = useGetLicenseKeyQuery({
    accessToken,
    account,
    licenseId
  })

  /**
   * What to put where the key goes.
   *
   * An empty string is what this rendered while the API refused every key
   * request, and it read as "this licence has no key" rather than as a
   * failure — the modal looked finished. Say which of the three it is.
   */
  const keyText = (() => {
    if (data?.licenseKey) return data.licenseKey
    if (isFetching) return t('licenses.modal.loadingKey')
    if (isError) return t('licenses.modal.keyUnavailable')
    return t('licenses.modal.loadingKey')
  })()

  const handleCopy = () => {
    setIsCopied(true)
    setTimeout(() => {
      setIsCopied(false)
      setAnchorEl(null)
    }, 3000)
  }

  const copyIcon = () => {
    return (
      <IconWrapper onClick={(event) => setAnchorEl(event.currentTarget)}>
        {isCopied ? (
          <Check size={isMobile ? 16 : 22} color={isMobile ? colors.white : colors.purple} />
        ) : (
          <Copy size={isMobile ? 16 : 22} color={isMobile ? colors.white : colors.purple} />
        )}
      </IconWrapper>
    )
  }

  return (
    <CopyToClipboard text={data?.licenseKey} onCopy={() => handleCopy()}>
      <MainWrapper>
        <Text color={colors.gray6}>{name}</Text>
        <Flex flexDirection={isMobile ? 'column' : 'row'} gap="16px">
          <Text
            variant={isMobile ? TextVariant.BODY_SMALL : TextVariant.BODY_DEFAULT}
            color={isError ? colors.red : undefined}
          >
            {keyText}
          </Text>
          {isMobile ? (
            <Button fullWidth mt="8px" endIcon={copyIcon()}>
              {isCopied ? 'Copied' : 'Copy'}
            </Button>
          ) : (
            <>
              <Popover
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                  vertical: -14,
                  horizontal: 14
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center'
                }}
              >
                <Text variant={TextVariant.BODY_DEFAULT} p="8px">
                  Copied!
                </Text>
              </Popover>
              {copyIcon()}
            </>
          )}
        </Flex>
      </MainWrapper>
    </CopyToClipboard>
  )
})

export default SuccessApiKey
